import { ChangeDetectionStrategy, Component, Input, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PaHttpClient } from 'src/app/clients/http-client';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';

export type AssignType = 'contractor' | 'delivery_contractor' | 'front_desk' | 'management' | 'provider' | 'guest';

@Component({
  selector: 'pa-assign-sp-popup',
  imports: [CommonModule, FormsModule, IconFieldModule, InputIconModule, InputTextModule],
  providers: [PaHttpClient],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './assign-sp-popup.component.html'
})
export class AssignSpPopupComponent implements OnInit {
  @Input() title = 'Select';
  @Input() assignType: AssignType = 'contractor';
  readonly excludeIds = input<string[]>([]);
  @Input() multiSelect = false;

  readonly close = output<any>();
  readonly isLoading = signal(false);
  readonly isLoadingMore = signal(false);
  readonly hasMore = signal(true);
  readonly providerSearch = signal('');
  readonly selectedItems = signal<any[]>([]);

  private readonly PAGE_LIMIT = 20;
  private currentPage = 1;
  private readonly allList = signal<any[]>([]);

  readonly filteredList = computed(() => {
    const q = this.providerSearch().trim().toLowerCase();
    const excluded = new Set(this.excludeIds().map(id => String(id)));
    const list = this.allList().filter(item => !excluded.has(String(item.user_id)));
    if (!q) return list;
    return list.filter(item => this.getDisplayName(item).toLowerCase().includes(q));
  });

  private readonly http = inject(PaHttpClient);

  ngOnInit(): void {
    this.fetchList();
  }

  private fetchList(append = false): void {
    if (append) {
       if (!this.hasMore() || this.isLoadingMore() || this.isLoading()) return;
      this.isLoadingMore.set(true);
    } else {
      this.isLoading.set(true);
      this.currentPage = 1;
      this.hasMore.set(true);
      this.allList.set([]);
    }

    const isManagement = this.assignType === 'management';
    const isGuest = this.assignType === 'guest';
    let endpoint: string;
    if (isManagement) {
      endpoint = 'request/managementList';
    } else if (isGuest) {
      endpoint = `contact_list?type=personal&page=${this.currentPage}&limit=${this.PAGE_LIMIT}`;
    } else {
      endpoint = `list_sp_contractor?page=${this.currentPage}&limit=${this.PAGE_LIMIT}`;
    }

    this.http.get(endpoint).subscribe({
      next: (res: any) => {
        const raw: any[] = Array.isArray(res?.data) ? res.data : [];
        const existingIds = new Set(this.allList().map((item: any) => String(item.user_id)));
        let mapped: any[];
        if (isManagement) {
          mapped = raw;
        } else if (isGuest) {
          mapped = raw.reduce((acc: any[], entry: any) => {
            const u = { ...entry, ...(entry.contactUser_details ?? {}) };
            if (u.user_id && !existingIds.has(String(u.user_id))) {
              existingIds.add(String(u.user_id));
              acc.push(u);
            }
            return acc;
          }, []);
        } else {
          mapped = this.mapItems(raw, existingIds);
        }

        if (append) {
          this.allList.update(prev => [...prev, ...mapped]);
          this.isLoadingMore.set(false);
        } else {
          this.allList.set(mapped);
          this.isLoading.set(false);
        }

        if (isManagement || raw.length < this.PAGE_LIMIT) {
          this.hasMore.set(false);
        } else {
          this.currentPage++;
        }
      },
      error: () => {
        if (append) this.isLoadingMore.set(false);
        else this.isLoading.set(false);
      },
    });
  }

  onScroll(event: Event): void {
    if (this.isLoading() || this.isLoadingMore() || !this.hasMore()) return;
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      this.fetchList(true);
    }
  }

  private mapItems(raw: any[], existingIds: Set<string>): any[] {
    if (this.assignType === 'provider') {
      return raw.reduce((acc: any[], entry: any) => {
        const sp = entry.sp_user;
        if (sp && !existingIds.has(String(sp.user_id))) {
          existingIds.add(String(sp.user_id));
          acc.push(sp);
        }
        return acc;
      }, []);
    }
    return raw.reduce((acc: any[], entry: any) => {
      const cu = entry.contractor_user;
      if (cu && !existingIds.has(String(cu.user_id))) {
        existingIds.add(String(cu.user_id));
        acc.push(cu);
      }
      return acc;
    }, []);
  }

  selectItem(item: any): void {
    if (!this.multiSelect) {
      this.close.emit(item);
      return;
    }
    const id = String(item.user_id);
    const current = this.selectedItems();
    const idx = current.findIndex(s => String(s.user_id) === id);
    if (idx >= 0) {
      this.selectedItems.set(current.filter((_, i) => i !== idx));
    } else {
      this.selectedItems.set([...current, item]);
    }
  }

  isSelected(item: any): boolean {
    return this.selectedItems().some(s => String(s.user_id) === String(item.user_id));
  }

  confirmSelection(): void {
    this.close.emit(this.selectedItems());
  }

  getDisplayName(item: any): string {
    return item.service_provider_name || item.management_name ||
      `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim() || '—';
  }

  getAvatar(item: any): string | null {
    const url = item.profile_picture || item.documents?.[0]?.url;
    return url && url.trim() ? url : null;
  }

  isPictureBlocked(status: string | null | undefined): boolean {
    return status === 'flagged' || status === 'blocked';
  }

  getInitials(item: any): string {
    const name = this.getDisplayName(item);
    if (!name || name === '—') return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  getLocation(item: any): string {
    const addr = item.addresses?.find((a: { address_type?: string }) => a.address_type === 'physical') ?? item.addresses?.[0];
    const stateObj = addr?.state ?? item.state;
    const city = addr?.city || item.city || '';
    const state =
      (typeof stateObj === 'string' ? stateObj : '') ||
      stateObj?.short_name ||
      stateObj?.state_name ||
      '';
    const zip = addr?.zipcode || item.zipcode || '';
    return [city, state, zip].filter(Boolean).join(', ');
  }
}
